import moment from 'moment'
import 'moment-timezone'

/** P3 (PV3-01-*), P3 Eco (PV3-02-*), P3X (PV3-03-*). */
const LEGACY_P3_MODEL_REGEX = /^PV3-(01|02|03)-/

/** Last day with graph data for legacy units (inclusive), in each unit's timezone. */
const LEGACY_P3_DATA_CUTOFF_DATE = '2026-06-12'

const DEFAULT_UNIT_TIMEZONE = 'Europe/London'

const LEGACY_UNIT_LIVE_VIEW_ERROR = 'legacy-unit-read-only'

function isLegacyP3Unit(unit) {
  if (!unit?.model) return false
  return LEGACY_P3_MODEL_REGEX.test(unit.model)
}

function customerOwnsOnlyLegacyUnits(ownUnits) {
  if (!ownUnits?.length) return false
  return ownUnits.every(isLegacyP3Unit)
}

function customerHasModernUnit(ownUnits) {
  if (!ownUnits?.length) return false
  return ownUnits.some((unit) => !isLegacyP3Unit(unit))
}

function getUnitTimezone(unit) {
  const tz = unit?.timezone || unit?.contractTimezone
  if (typeof tz === 'string' && tz.length > 0) return tz
  return DEFAULT_UNIT_TIMEZONE
}

/** Start of the last chart day legacy units can show (for date pickers / navigation). */
function getLegacyP3LastChartDayStart(unitTz = DEFAULT_UNIT_TIMEZONE) {
  return moment.tz(LEGACY_P3_DATA_CUTOFF_DATE, 'YYYY-MM-DD', unitTz).startOf('day')
}

/** End of the legacy graph data window in the given timezone. */
function getLegacyP3DataCutoffEnd(unitTz = DEFAULT_UNIT_TIMEZONE) {
  return moment.tz(LEGACY_P3_DATA_CUTOFF_DATE, 'YYYY-MM-DD', unitTz).endOf('day')
}

function selectedUnitsIncludeLegacyP3(units) {
  if (!units?.length) return false
  return units.some(isLegacyP3Unit)
}

/**
 * Chart CSV is for sunsetted products (legacy P3) plus contract IDs in
 * REACT_APP_CHART_CSV_EXPORT_CONTRACT_IDS (comma-separated).
 */
const CHART_CSV_EXPORT_CONTRACT_IDS = new Set(
  String(process.env.REACT_APP_CHART_CSV_EXPORT_CONTRACT_IDS || '')
    .split(',')
    .map((id) => id.trim().toUpperCase())
    .filter(Boolean)
)

function unitHasChartCsvExport(unit) {
  if (isLegacyP3Unit(unit)) return true
  const configId = String(unit?.configId || '').toUpperCase()
  return CHART_CSV_EXPORT_CONTRACT_IDS.has(configId)
}

function selectedUnitsHaveChartCsvExport(units) {
  if (!units?.length) return false
  return units.some(unitHasChartCsvExport)
}

/** First calendar day a full-export contract may include in a chart CSV. */
const CHART_CSV_FULL_EXPORT_EARLIEST_DATE = '2025-01-01'

const CHART_CSV_EXPORT_HISTORY_MAX_DAYS = 90

/**
 * Contracts in REACT_APP_CHART_CSV_FULL_EXPORT_CONTRACT_IDS (comma-separated)
 * can export back to CHART_CSV_FULL_EXPORT_EARLIEST_DATE. Other CSV contracts
 * stay on the rolling 90-day window.
 */
const CHART_CSV_FULL_EXPORT_CONTRACT_IDS = new Set(
  String(process.env.REACT_APP_CHART_CSV_FULL_EXPORT_CONTRACT_IDS || '')
    .split(',')
    .map((id) => id.trim().toUpperCase())
    .filter(Boolean)
)

function unitHasChartCsvFullExport(unit) {
  const configId = String(unit?.configId || '').toUpperCase()
  return CHART_CSV_FULL_EXPORT_CONTRACT_IDS.has(configId)
}

function selectedUnitsHaveChartCsvFullExport(units) {
  if (!units?.length) return false
  return units.some(unitHasChartCsvFullExport)
}

/**
 * Earliest day included in the CSV date presets, in the unit timezone.
 * Full-export contracts start at 2025-01-01. Everyone else is 90 days
 * before the latest chart day (legacy cutoff when that still applies).
 */
function getChartCsvExportEarliestDay(units, todayStart = null) {
  const today = getMaxChartDayForUnits(units, todayStart).clone().startOf('day')
  if (!selectedUnitsHaveChartCsvFullExport(units)) {
    return today.clone().subtract(CHART_CSV_EXPORT_HISTORY_MAX_DAYS, 'days').startOf('day')
  }

  const unit = units.find((item) => unitHasChartCsvFullExport(item))
  const floor = moment
    .tz(CHART_CSV_FULL_EXPORT_EARLIEST_DATE, 'YYYY-MM-DD', getUnitTimezone(unit))
    .startOf('day')
  if (floor.isAfter(today)) return today.clone()
  return floor
}

function allSelectedUnitsAreLegacyP3(units) {
  if (!units?.length) return false
  return units.every(isLegacyP3Unit)
}

/**
 * Chart date cap and read-only UI apply only when every selected unit is legacy.
 * Mixed clusters (legacy + modern) keep full access while the modern unit sends data.
 */
function shouldApplyLegacyP3Restrictions(units) {
  return allSelectedUnitsAreLegacyP3(units)
}

/**
 * Latest selectable chart day for the current unit selection.
 * Uses unit timezone (P3Contract.timezone when exposed, else Europe/London).
 */
function getMaxChartDayForUnits(units, todayStart = null) {
  const today =
    todayStart?.clone?.() ||
    moment().tz(DEFAULT_UNIT_TIMEZONE).startOf('day')

  if (!shouldApplyLegacyP3Restrictions(units)) return today

  let maxDay = today.clone()
  for (const unit of units) {
    const unitLastDay = getLegacyP3LastChartDayStart(getUnitTimezone(unit))
    if (unitLastDay.isBefore(maxDay, 'day')) maxDay = unitLastDay
  }
  return maxDay
}

function clampChartDayToLegacyLimit(dayLocal, units) {
  const day = dayLocal?.clone?.()
  if (!day) return dayLocal
  const maxDay = getMaxChartDayForUnits(units)
  if (day.isAfter(maxDay, 'day')) return maxDay.clone()
  return day
}

function isLegacyUnitLiveViewResponse(response) {
  return response?.errorMessage === LEGACY_UNIT_LIVE_VIEW_ERROR
}

function countReportingP3Units(units) {
  if (!units?.length) return 0
  return units.filter((unit) => !isLegacyP3Unit(unit)).length
}

/**
 * Header suffix for multi-unit Live View / Charts / Totals, e.g.
 * "(2 units, 1 reporting)" or "(3 units, in 2 clusters)".
 */
function formatSelectedUnitsCountSuffix(units, clusterCount = 0, getEnglishText) {
  if (!units?.length || units.length <= 1) return null

  const total = units.length
  const reportingCount = countReportingP3Units(units)
  const mixedLegacyModern = reportingCount > 0 && reportingCount < total

  const segments = [`${total} ${getEnglishText('common : units')}`]
  if (mixedLegacyModern) {
    segments.push(`${reportingCount} ${getEnglishText('common : units-reporting')}`)
  }
  if (clusterCount > 1) {
    segments.push(`in ${clusterCount} clusters`)
  }
  return `(${segments.join(', ')})`
}

export {
  LEGACY_P3_MODEL_REGEX,
  LEGACY_P3_DATA_CUTOFF_DATE,
  LEGACY_UNIT_LIVE_VIEW_ERROR,
  DEFAULT_UNIT_TIMEZONE,
  isLegacyP3Unit,
  customerOwnsOnlyLegacyUnits,
  customerHasModernUnit,
  getUnitTimezone,
  getLegacyP3LastChartDayStart,
  getLegacyP3DataCutoffEnd,
  selectedUnitsIncludeLegacyP3,
  selectedUnitsHaveChartCsvExport,
  selectedUnitsHaveChartCsvFullExport,
  getChartCsvExportEarliestDay,
  CHART_CSV_FULL_EXPORT_EARLIEST_DATE,
  CHART_CSV_EXPORT_HISTORY_MAX_DAYS,
  allSelectedUnitsAreLegacyP3,
  getMaxChartDayForUnits,
  clampChartDayToLegacyLimit,
  isLegacyUnitLiveViewResponse,
  shouldApplyLegacyP3Restrictions,
  countReportingP3Units,
  formatSelectedUnitsCountSuffix
}
